import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Download, Users, Calendar, DollarSign, GraduationCap, Receipt, Award } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { getAuthHeader } = useAuth();
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [fees, setFees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [reportType, setReportType] = useState('attendance');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedFee, setSelectedFee] = useState('');
  const [dateFrom, setDateFrom] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, teachersRes, classesRes, examsRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/exams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/accounts/transactions`, { headers: getAuthHeader() })
      ]);
      
      // Handle paginated responses
      setStudents(studentsRes.data?.items || studentsRes.data || []);
      setTeachers(teachersRes.data?.items || teachersRes.data || []);
      setClasses(classesRes.data || []);
      setExams(examsRes.data || []);
      setTransactions(transactionsRes.data || []);
      
      // Fetch fee and attendance data separately with error handling
      try {
        // Use fee-management payments instead of /api/fees
        const paymentsRes = await axios.get(`${API_URL}/api/fee-management/payments?page_size=100`, { headers: getAuthHeader() });
        const payments = paymentsRes.data?.items || [];
        // Transform to fee format - use amount_paid field
        setFees(payments.map(p => ({
          id: p.id,
          student_id: p.student_id,
          student_name: p.student_name,
          amount: p.amount_paid || 0,
          paid_amount: p.amount_paid || 0,
          receipt_number: p.receipt_number,
          payment_date: p.payment_date,
          fee_head_name: p.fee_head_name || 'Fee',
          class_name: p.class_name
        })));
      } catch (e) {
        console.log('Payments API not available');
        setFees([]);
      }
      
      // Note: Use fee-management reports for detailed attendance/fee data
      setAttendance([]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load reports data');
    }
  };

  const generateAttendanceReport = () => {
    const filteredAttendance = attendance.filter(a => {
      const date = new Date(a.date);
      return date >= new Date(dateFrom) && date <= new Date(dateTo);
    });

    if (selectedClass) {
      const classStudents = students.filter(s => s.class_id === selectedClass);
      const studentIds = classStudents.map(s => s.id);
      return filteredAttendance.filter(a => studentIds.includes(a.student_id));
    }

    return filteredAttendance;
  };

  const generateFeeReport = () => {
    const filteredFees = fees.filter(f => {
      const dueDate = new Date(f.due_date);
      return dueDate >= new Date(dateFrom) && dueDate <= new Date(dateTo);
    });

    const totalAmount = filteredFees.reduce((sum, f) => sum + f.amount, 0);
    const collectedAmount = filteredFees.reduce((sum, f) => sum + f.paid_amount, 0);
    const pendingAmount = totalAmount - collectedAmount;

    return {
      fees: filteredFees,
      stats: {
        total: totalAmount,
        collected: collectedAmount,
        pending: pendingAmount,
        count: filteredFees.length
      }
    };
  };

  const generateFinancialReport = () => {
    const filteredTransactions = transactions.filter(t => {
      const date = new Date(t.date);
      return date >= new Date(dateFrom) && date <= new Date(dateTo);
    });

    const income = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expense = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);

    return {
      transactions: filteredTransactions,
      stats: {
        income,
        expense,
        balance: income - expense,
        count: filteredTransactions.length
      }
    };
  };

  const generateStudentReport = () => {
    if (!selectedStudent) return null;

    const student = students.find(s => s.id === selectedStudent);
    const studentAttendance = attendance.filter(a => a.student_id === selectedStudent);
    const studentFees = fees.filter(f => f.student_id === selectedStudent);
    const studentClass = classes.find(c => c.id === student?.class_id);

    const presentCount = studentAttendance.filter(a => a.status === 'present').length;
    const totalCount = studentAttendance.length;
    const attendancePercentage = totalCount > 0 ? ((presentCount / totalCount) * 100).toFixed(2) : 0;

    return {
      student,
      class: studentClass,
      attendance: {
        present: presentCount,
        total: totalCount,
        percentage: attendancePercentage
      },
      fees: studentFees
    };
  };

  // Download PDF from backend
  const downloadPDF = async (endpoint, filename) => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Download Fee Receipt PDF
  const downloadFeeReceipt = async (paymentId) => {
    // Use fee-management payment receipt API
    await downloadPDF(`/api/fee-management/payment-receipt/${paymentId}/pdf`, `fee_receipt_${paymentId.slice(0, 8)}.pdf`);
  };

  // Download Student Report Card PDF
  const downloadStudentReport = async (studentId) => {
    const student = students.find(s => s.id === studentId);
    await downloadPDF(`/api/reports/student-report/${studentId}`, `report_card_${student?.student_id || studentId.slice(0, 8)}.pdf`);
  };

  // Download Attendance Certificate PDF
  const downloadAttendanceCertificate = async (studentId) => {
    const student = students.find(s => s.id === studentId);
    await downloadPDF(`/api/reports/attendance-certificate/${studentId}`, `attendance_cert_${student?.student_id || studentId.slice(0, 8)}.pdf`);
  };

  const downloadJSONReport = async () => {
    try {
      toast.info('Generating report...');
      
      let reportData = {};
      
      switch(reportType) {
        case 'attendance':
          reportData = {
            type: 'attendance',
            data: generateAttendanceReport(),
            dateRange: { from: dateFrom, to: dateTo }
          };
          break;
        case 'fee':
          reportData = {
            type: 'fee',
            ...generateFeeReport(),
            dateRange: { from: dateFrom, to: dateTo }
          };
          break;
        case 'financial':
          reportData = {
            type: 'financial',
            ...generateFinancialReport(),
            dateRange: { from: dateFrom, to: dateTo }
          };
          break;
        case 'student':
          reportData = {
            type: 'student',
            ...generateStudentReport()
          };
          break;
        default:
          break;
      }

      const dataStr = JSON.stringify(reportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  const attendanceReportData = generateAttendanceReport();
  const feeReportData = generateFeeReport();
  const financialReportData = generateFinancialReport();
  const studentReportData = generateStudentReport();

  // Get filtered students by class
  const filteredStudents = selectedClass 
    ? students.filter(s => s.class_id === selectedClass)
    : students;

  return (
    <DashboardLayout title="Reports & Analytics">
      <div className="space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Students</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{students.length}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Teachers</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{teachers.length}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Attendance Rate</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {attendance.length > 0 ? 
                    ((attendance.filter(a => a.status === 'present').length / attendance.length) * 100).toFixed(1) 
                    : 0}%
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Fee Collection</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {fees.length > 0 && fees.reduce((sum, f) => sum + f.amount, 0) > 0 ?
                    ((fees.reduce((sum, f) => sum + f.paid_amount, 0) / fees.reduce((sum, f) => sum + f.amount, 0)) * 100).toFixed(1)
                    : 0}%
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* PDF Downloads Section */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Download PDF Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Fee Receipt */}
              <div className="space-y-3 p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Receipt className="h-4 w-4 text-green-600" />
                  Fee Receipt
                </div>
                <Select value={selectedFee} onValueChange={setSelectedFee}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select fee record" />
                  </SelectTrigger>
                  <SelectContent>
                    {fees.map(fee => (
                        <SelectItem key={fee.id} value={fee.id}>
                          {fee.student_name || 'Unknown'} - {fee.receipt_number || fee.fee_head_name} (₹{fee.amount})
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => selectedFee && downloadFeeReceipt(selectedFee)}
                  disabled={!selectedFee || downloading}
                  className="w-full"
                  variant="outline"
                  data-testid="download-fee-receipt-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Receipt
                </Button>
              </div>

              {/* Student Report Card */}
              <div className="space-y-3 p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <GraduationCap className="h-4 w-4 text-purple-600" />
                  Student Report Card
                </div>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map(student => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.student_id || student.roll_number} - {student.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => selectedStudent && downloadStudentReport(selectedStudent)}
                  disabled={!selectedStudent || downloading}
                  className="w-full"
                  variant="outline"
                  data-testid="download-report-card-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Report Card
                </Button>
              </div>

              {/* Attendance Certificate */}
              <div className="space-y-3 p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Award className="h-4 w-4 text-amber-600" />
                  Attendance Certificate
                </div>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map(student => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.student_id || student.roll_number} - {student.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => selectedStudent && downloadAttendanceCertificate(selectedStudent)}
                  disabled={!selectedStudent || downloading}
                  className="w-full"
                  variant="outline"
                  data-testid="download-attendance-cert-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Certificate
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Generation - JSON/CSV */}
        <Card>
          <CardHeader>
            <CardTitle>Generate Data Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Report Type</Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attendance">Attendance Report</SelectItem>
                      <SelectItem value="fee">Fee Collection Report</SelectItem>
                      <SelectItem value="financial">Financial Report</SelectItem>
                      <SelectItem value="student">Student Summary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {reportType === 'student' ? (
                  <div className="space-y-2">
                    <Label>Select Student</Label>
                    <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose student" />
                      </SelectTrigger>
                      <SelectContent>
                        {students.map(student => (
                          <SelectItem key={student.id} value={student.id}>
                            {student.roll_number || student.student_id} - {student.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Filter by Class (Optional)</Label>
                    <Select value={selectedClass || "all"} onValueChange={(val) => setSelectedClass(val === "all" ? "" : val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All classes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {classes.map(cls => (
                          <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {reportType !== 'student' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <Button onClick={downloadJSONReport} className="w-full md:w-auto" data-testid="download-json-report-btn">
                <Download className="h-4 w-4 mr-2" />
                Download JSON Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Report Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={reportType} className="w-full">
              <TabsContent value="attendance">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold">Attendance Report</h3>
                    <p className="text-sm text-muted-foreground">
                      {dateFrom} to {dateTo}
                    </p>
                  </div>
                  <div className="border rounded p-4">
                    <p className="text-sm">Total Records: {attendanceReportData.length}</p>
                    <p className="text-sm">Present: {attendanceReportData.filter(a => a.status === 'present').length}</p>
                    <p className="text-sm">Absent: {attendanceReportData.filter(a => a.status === 'absent').length}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="fee">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold">Fee Collection Report</h3>
                    <p className="text-sm text-muted-foreground">
                      {dateFrom} to {dateTo}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border rounded p-4">
                      <p className="text-sm text-muted-foreground">Total Amount</p>
                      <p className="text-2xl font-bold">₹{feeReportData.stats.total.toFixed(2)}</p>
                    </div>
                    <div className="border rounded p-4">
                      <p className="text-sm text-muted-foreground">Collected</p>
                      <p className="text-2xl font-bold text-green-600">₹{feeReportData.stats.collected.toFixed(2)}</p>
                    </div>
                    <div className="border rounded p-4">
                      <p className="text-sm text-muted-foreground">Pending</p>
                      <p className="text-2xl font-bold text-red-600">₹{feeReportData.stats.pending.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="financial">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold">Financial Report</h3>
                    <p className="text-sm text-muted-foreground">
                      {dateFrom} to {dateTo}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border rounded p-4">
                      <p className="text-sm text-muted-foreground">Total Income</p>
                      <p className="text-2xl font-bold text-green-600">₹{financialReportData.stats.income.toFixed(2)}</p>
                    </div>
                    <div className="border rounded p-4">
                      <p className="text-sm text-muted-foreground">Total Expense</p>
                      <p className="text-2xl font-bold text-red-600">₹{financialReportData.stats.expense.toFixed(2)}</p>
                    </div>
                    <div className="border rounded p-4">
                      <p className="text-sm text-muted-foreground">Balance</p>
                      <p className="text-2xl font-bold text-blue-600">₹{financialReportData.stats.balance.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="student">
                {studentReportData ? (
                  <div className="space-y-4">
                    <div className="border rounded p-4">
                      <h3 className="font-semibold text-lg">{studentReportData.student?.name}</h3>
                      <p className="text-sm text-muted-foreground">Roll No: {studentReportData.student?.roll_number || studentReportData.student?.student_id}</p>
                      <p className="text-sm text-muted-foreground">Class: {studentReportData.class?.name || 'N/A'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="border rounded p-4">
                        <p className="text-sm text-muted-foreground">Attendance</p>
                        <p className="text-2xl font-bold">{studentReportData.attendance.percentage}%</p>
                        <p className="text-xs text-muted-foreground">
                          {studentReportData.attendance.present} / {studentReportData.attendance.total} days
                        </p>
                      </div>
                      <div className="border rounded p-4">
                        <p className="text-sm text-muted-foreground">Fee Status</p>
                        <p className="text-2xl font-bold">
                          ₹{studentReportData.fees.reduce((sum, f) => sum + f.paid_amount, 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-muted-foreground">Paid</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Select a student to view report</p>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
